import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Plus, Loader2, ArrowLeft, Copy, Archive, ArchiveRestore, Trash2,
  ExternalLink, MoreHorizontal, Rocket, Sparkles, Eye,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const httpAuth = () => ({ Authorization: `Bearer ${localStorage.getItem("wt_token")}` });

/**
 * /ad-builder/library — grid of a signed-in user's saved ad projects.
 * Actions per card: Open · Duplicate · Archive / Restore · Delete.
 */
export default function AdLibrary() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [busyId, setBusyId] = useState("");
  const [authError, setAuthError] = useState("");

  const load = async () => {
    setItems(null); setAuthError("");
    try {
      const { data } = await axios.get(
        `${API}/ad-builder/projects?include_archived=${showArchived}`,
        { headers: httpAuth() });
      setItems(data.projects || []);
    } catch (e) {
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        setAuthError("Please sign in to view your saved ads.");
      } else {
        toast.error("Failed to load ads");
      }
      setItems([]);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showArchived]);

  const duplicate = async (pid) => {
    setBusyId(pid);
    try {
      const { data } = await axios.post(`${API}/ad-builder/projects/${pid}/duplicate`,
        {}, { headers: httpAuth() });
      toast.success("Duplicated");
      nav(`/ad-builder?resume=${data.project.id}`);
    } catch (e) { toast.error("Duplicate failed"); }
    finally { setBusyId(""); }
  };

  const setArchived = async (pid, archived) => {
    setBusyId(pid);
    try {
      await axios.post(`${API}/ad-builder/projects/${pid}/archive`,
        { archived }, { headers: httpAuth() });
      toast.success(archived ? "Archived" : "Restored");
      load();
    } catch (e) { toast.error("Failed"); }
    finally { setBusyId(""); }
  };

  const remove = async (pid) => {
    if (!confirm("Delete this ad? This can't be undone.")) return;
    setBusyId(pid);
    try {
      await axios.delete(`${API}/ad-builder/projects/${pid}`,
        { headers: httpAuth() });
      toast.success("Deleted");
      load();
    } catch (e) { toast.error("Delete failed"); }
    finally { setBusyId(""); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-fuchsia-950 text-white pb-20"
         data-testid="ad-library">
      <div className="fixed inset-0 opacity-[0.03] pointer-events-none mix-blend-overlay"
           style={{ backgroundImage:
             "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='80' height='80'><filter id='n'><feTurbulence baseFrequency='0.9'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>\")" }} />

      <div className="relative max-w-6xl mx-auto px-6 pt-14">
        <Link to="/ad-builder"
              className="text-white/50 hover:text-white text-xs inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> New ad
        </Link>
        <div className="mt-3 text-[10px] uppercase tracking-[0.35em] text-fuchsia-400 font-bold">
          WebsiteTech · Ad Builder
        </div>
        <div className="flex items-end justify-between gap-4 flex-wrap mt-3">
          <h1 className="text-4xl sm:text-5xl font-bold">My Ads</h1>
          <div className="flex gap-2">
            <button onClick={() => setShowArchived(v => !v)}
                    data-testid="ad-library-archived-toggle"
                    className={`rounded-lg px-3 py-2 text-xs font-bold ${
                      showArchived ? "bg-white text-slate-900" : "bg-white/10 hover:bg-white/20"}`}>
              {showArchived ? "Hide archived" : "Show archived"}
            </button>
            <Link to="/ad-builder"
                  data-testid="ad-library-new-btn"
                  className="rounded-lg bg-gradient-to-r from-fuchsia-500 to-orange-400 text-white px-3 py-2 text-xs font-bold inline-flex items-center gap-1">
              <Plus className="w-3.5 h-3.5" /> New ad
            </Link>
          </div>
        </div>

        {authError && (
          <div className="mt-8 rounded-2xl bg-amber-500/10 ring-1 ring-amber-500/30 text-amber-100 p-6 max-w-2xl">
            {authError}
          </div>
        )}

        {items === null && (
          <div className="mt-10 flex items-center gap-2 text-white/60">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        )}

        {items && items.length === 0 && !authError && (
          <div className="mt-16 text-center">
            <Sparkles className="w-10 h-10 mx-auto text-fuchsia-300" />
            <div className="mt-3 text-xl font-bold">No ads yet</div>
            <div className="text-white/60">Create your first ad in 60 seconds.</div>
            <Link to="/ad-builder"
                  data-testid="ad-library-first-btn"
                  className="mt-4 inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-fuchsia-500 to-orange-400 text-white px-5 py-3 font-bold">
              <Plus className="w-4 h-4" /> Start
            </Link>
          </div>
        )}

        {items && items.length > 0 && (
          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-3 gap-4"
               data-testid="ad-library-grid">
            {items.map(p => (
              <AdCard key={p.id} project={p} busy={busyId === p.id}
                       onOpen={() => nav(`/ad-builder?resume=${p.id}`)}
                       onDuplicate={() => duplicate(p.id)}
                       onArchive={() => setArchived(p.id, !p.archived)}
                       onDelete={() => remove(p.id)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function AdCard({ project, busy, onOpen, onDuplicate, onArchive, onDelete }) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  const manual = project.manual || {};
  const brand = project.brand || {};
  const biz = manual.business_name || brand.business_name?.value || "Untitled ad";
  const primary = project.renders?.["1x1"] || project.renders?.landscape;
  const ready = project.readiness?.band;
  const pub = project.published;
  return (
    <div className="rounded-2xl bg-white/5 ring-1 ring-white/10 backdrop-blur overflow-hidden flex flex-col"
         data-testid={`ad-card-${project.id}`}>
      <div className="aspect-video bg-black/40 relative">
        {primary ? (
          <img src={`${backend}${primary.url}`} alt=""
                className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/30">
            <Sparkles className="w-8 h-8" />
          </div>
        )}
        {pub && (
          <span className="absolute top-2 right-2 rounded-full bg-emerald-500 text-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest inline-flex items-center gap-1">
            <Rocket className="w-3 h-3" /> live
          </span>
        )}
        {project.archived && (
          <span className="absolute top-2 left-2 rounded-full bg-amber-500 text-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest">
            archived
          </span>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <h3 className="font-bold text-white truncate">{biz}</h3>
        <div className="text-xs text-white/50 mt-1 truncate">
          {project.url || manual.category || "manual entry"}
        </div>
        {ready && (
          <div className="mt-2">
            <span className={`text-[10px] font-bold uppercase tracking-widest rounded-full px-2 py-0.5 ${
              ready === "ready" ? "bg-emerald-500/30 text-emerald-200"
               : ready === "almost" ? "bg-amber-500/30 text-amber-200"
               : "bg-rose-500/30 text-rose-200"}`}>
              {ready}
            </span>
            {project.readiness?.score !== undefined && (
              <span className="ml-2 text-xs text-white/50">{project.readiness.score}/100</span>
            )}
          </div>
        )}
        <div className="mt-auto pt-4 flex items-center gap-1 flex-wrap">
          <button onClick={onOpen} disabled={busy}
                  data-testid={`ad-card-open-${project.id}`}
                  className="inline-flex items-center gap-1 rounded-lg bg-fuchsia-500 hover:bg-fuchsia-400 text-white px-3 py-1.5 text-xs font-bold">
            <Eye className="w-3 h-3" /> Open
          </button>
          <button onClick={onDuplicate} disabled={busy}
                  data-testid={`ad-card-duplicate-${project.id}`}
                  className="inline-flex items-center gap-1 rounded-lg bg-white/10 hover:bg-white/20 text-white/80 px-3 py-1.5 text-xs font-bold">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Copy className="w-3 h-3" />} Duplicate
          </button>
          <button onClick={onArchive} disabled={busy}
                  data-testid={`ad-card-archive-${project.id}`}
                  className="inline-flex items-center gap-1 rounded-lg bg-white/10 hover:bg-white/20 text-white/80 px-2 py-1.5 text-xs font-bold">
            {project.archived ? <ArchiveRestore className="w-3 h-3" /> : <Archive className="w-3 h-3" />}
          </button>
          <button onClick={onDelete} disabled={busy}
                  data-testid={`ad-card-delete-${project.id}`}
                  className="inline-flex items-center gap-1 rounded-lg bg-rose-500/20 hover:bg-rose-500/40 text-rose-200 px-2 py-1.5 text-xs font-bold">
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>
  );
}
